import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { PiX, PiTextStrikethrough, PiSquare } from "react-icons/pi";

import API from "../../api";
import { capture } from "../../instrument";
import ShortcutHint from "../shortcuts/ShortcutHint";
import ExamSearchInput from "./ExamSearchInput";

const InspectorPanel = ({
  doc,
  categories,
  entityGroups,
  recommendations,
  missingCategories,
  struckStarts,
  uncheckedStarts,
  textByStart,
  scoreByStart,
  zoneEntities,
  highlightedEntity,
  onAcceptRecommendation,
  onRejectRecommendation,
  onRemoveToken,
  onToggleStruck,
  onToggleUnchecked,
  onEditEntityText,
  onToggleZoneStruck,
  onToggleZoneUnchecked,
  onEditZoneText,
  onRemoveZone,
  onLocate,
  onSelectCategory,
}) => {
  const [expandedActInfo, setExpandedActInfo] = useState({});
  const [editing, setEditing] = useState(null);
  const highlightedRef = useRef(null);

  useEffect(() => {
    setExpandedActInfo({});
    setEditing(null);
  }, [doc?._id]);

  useEffect(() => {
    highlightedRef.current?.scrollIntoView({ block: "nearest" });
  }, [highlightedEntity]);

  const current = recommendations[0];
  const modifiedActs = (doc?.acts || []).filter((act) => (act.isAdded === true || act.isDeleted === true) && (act.codes?.length > 0 || act.codesPredicted?.length > 0));
  const zonesByLabel = {};
  (zoneEntities || []).forEach((zone, zoneIndex) => (zonesByLabel[zone.label] = zonesByLabel[zone.label] || []).push({ zone, zoneIndex }));
  const groupsWithItems = [...new Set([...Object.keys(entityGroups).filter((key) => entityGroups[key].items.length > 0), ...Object.keys(zonesByLabel)])];

  const onActClick = async (act, key) => {
    if (expandedActInfo[key]) {
      setExpandedActInfo((previous) => ({ ...previous, [key]: null }));
      return;
    }

    try {
      const codes = (act.codes?.length > 0 ? act.codes : act.codesPredicted).join(",");
      const response = await API.get(`/table?indexName=${doc.indexName}&codes=${codes}`);
      setExpandedActInfo((previous) => ({ ...previous, [key]: response.data || [] }));
    } catch (error) {
      capture(error);
      toast.error("Erreur lors de la récupération des informations de l'acte");
    }
  };

  return (
    <div className="flex h-full w-80 shrink-0 flex-col overflow-y-auto border-l border-gray-200 bg-white">
      {current && (
        <div className="border-b border-gray-100 p-4">
          <div className="flex items-baseline justify-between">
            <div className="section-title">Révision</div>
            <div className="text-xs tabular-nums text-gray-400">1/{recommendations.length}</div>
          </div>
          <div className="mt-3 rounded-lg border border-gray-200 p-3">
            <button type="button" className="w-full text-left text-sm font-medium text-gray-900 hover:underline" onClick={() => onLocate(current.indexes)}>
              {current.tokens.join(" ")}
            </button>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
              <span>{current.currentTag || "O"}</span>
              <span>→</span>
              {current.tag === "O" ? (
                <span className="font-medium text-gray-900">Retirer le label</span>
              ) : (
                <span className="flex items-center gap-1 font-medium text-gray-900">
                  <span className={`h-1.5 w-1.5 rounded-full ${categories.find((c) => c.name === current.tag)?.bg || "bg-gray-400"}`} />
                  {current.tag}
                </span>
              )}
            </div>
            <div className="mt-2.5 flex gap-2">
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary-50 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-100"
                onClick={() => onAcceptRecommendation(current.indexes)}
              >
                Accepter <ShortcutHint id="ANNOTATION_ACCEPT" />
              </button>
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
                onClick={() => onRejectRecommendation(current.indexes)}
              >
                Rejeter <ShortcutHint id="ANNOTATION_REJECT" />
              </button>
            </div>
          </div>
          {recommendations.length > 1 && <div className="mt-2 text-xs text-gray-400">{recommendations.length - 1} autre(s) en attente</div>}
        </div>
      )}

      <div className="flex-1 p-4">
        <div className="flex items-baseline justify-between">
          <div className="flex items-baseline gap-2">
            <div className="section-title">Entités</div>
            {missingCategories.length > 0 && <span className="text-xs font-medium tabular-nums text-orange-600">{missingCategories.length} manquante(s)</span>}
          </div>
          <div className="text-xs tabular-nums text-gray-400">
            {groupsWithItems.reduce((sum, key) => sum + (entityGroups[key]?.items.length || 0) + (zonesByLabel[key]?.length || 0), 0)}
          </div>
        </div>

        {missingCategories.length > 0 && (
          <div className="mt-2 flex flex-col border-b border-gray-100 pb-2">
            {missingCategories.map((name) => (
              <button
                key={name}
                type="button"
                className="flex items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-orange-50"
                title="Sélectionner ce label pour annoter"
                onClick={() => onSelectCategory(categories.find((category) => category.name === name))}
              >
                <span className="h-2 w-2 shrink-0 rounded-full border border-orange-500" />
                <span className="min-w-0 flex-1 truncate text-sm text-orange-700">{name}</span>
                <span className="shrink-0 text-[10px] font-medium uppercase text-orange-500">manquant</span>
              </button>
            ))}
          </div>
        )}

        {groupsWithItems.length === 0 && missingCategories.length === 0 && <div className="mt-6 text-center text-sm text-gray-400">Aucune entité annotée</div>}

        <div className="mt-2 flex flex-col">
          {groupsWithItems.map((key) => (
            <div key={key} className="border-b border-gray-100 py-2 last:border-b-0">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${categories.find((c) => c.name === key)?.bg || "bg-gray-400"}`} />
                <span className="text-xs font-medium text-gray-600">{key}</span>
                <span className="text-xs tabular-nums text-gray-400">{(entityGroups[key]?.items.length || 0) + (zonesByLabel[key]?.length || 0)}</span>
              </div>
              <div className="mt-1 flex flex-col">
                {(entityGroups[key]?.items || []).map((entity, index) => {
                  const start = entity.indexes[0];
                  const isStruck = struckStarts?.has(start);
                  const isUnchecked = uncheckedStarts?.has(start);
                  const overrideText = textByStart?.get(start);
                  const score = scoreByStart?.get(start);
                  const displayText = overrideText ?? entity.contents.map((content) => (content === "\n" ? "↵" : content || "_")).join(" ");
                  const editText = overrideText ?? entity.contents.filter((content) => content && content !== "\n").join(" ");
                  const isEditing = editing?.start === start;
                  const isHighlighted = highlightedEntity?.start === start;
                  return (
                    <div
                      key={index}
                      ref={isHighlighted ? highlightedRef : undefined}
                      className={`group flex items-center gap-2 rounded-md px-1.5 py-1 ${isHighlighted ? "bg-primary-50/70" : "hover:bg-gray-50"}`}
                    >
                      {isEditing ? (
                        <ExamSearchInput
                          value={editing.value}
                          onChange={(text) => setEditing({ start, value: text })}
                          onPick={(text) => {
                            onEditEntityText(entity.indexes, text);
                            setEditing(null);
                          }}
                          enabled={key === "ExamName"}
                          autoFocus
                          onEnter={() => {
                            onEditEntityText(entity.indexes, editing.value.trim());
                            setEditing(null);
                          }}
                          onEscape={() => setEditing(null)}
                          onBlur={() => setEditing(null)}
                        />
                      ) : (
                        <button
                          type="button"
                          className={`min-w-0 flex-1 truncate text-left text-sm ${isStruck ? "text-gray-400 line-through" : isUnchecked ? "text-gray-400" : overrideText ? "font-medium text-primary-700" : "text-gray-800"}`}
                          title="Cliquer pour localiser · double-cliquer pour corriger le texte"
                          onClick={() => onLocate(entity.indexes)}
                          onDoubleClick={() => setEditing({ start, value: editText })}
                        >
                          {displayText}
                        </button>
                      )}
                      {isUnchecked && !isEditing && <span className="shrink-0 rounded bg-gray-100 px-1 text-[10px] font-medium text-gray-500">non coché</span>}
                      {score != null && score < 0.5 && !isEditing && <span className="shrink-0 rounded bg-amber-50 px-1 text-[10px] font-medium text-amber-700">faible</span>}
                      <button
                        type="button"
                        aria-label={isUnchecked ? "Annuler le non coché" : "Marquer non coché"}
                        title={isUnchecked ? "Annuler le non coché" : "Marquer comme case non cochée (exclu de l'extraction)"}
                        className={`shrink-0 rounded p-0.5 ${isUnchecked ? "text-gray-700 hover:bg-gray-100" : "text-gray-300 hover:bg-gray-100 hover:text-gray-700"}`}
                        onClick={() => onToggleUnchecked(entity.indexes)}
                      >
                        <PiSquare className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        aria-label={isStruck ? "Annuler le barré" : "Marquer barré"}
                        title={isStruck ? "Annuler le barré" : "Marquer comme barré (exclu de l'extraction)"}
                        className={`shrink-0 rounded p-0.5 ${isStruck ? "text-amber-600 hover:bg-amber-50" : "text-gray-300 hover:bg-amber-50 hover:text-amber-600"}`}
                        onClick={() => onToggleStruck(entity.indexes)}
                      >
                        <PiTextStrikethrough className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        aria-label="Retirer l'entité"
                        className="shrink-0 rounded p-0.5 text-gray-300 hover:bg-red-50 hover:text-red-600"
                        onClick={() => onRemoveToken(entity.indexes)}
                      >
                        <PiX className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
                {(zonesByLabel[key] || []).map(({ zone, zoneIndex }) => {
                  const isUnchecked = zone.active === false && zone.reason === "unchecked";
                  const isStruck = zone.active === false && zone.reason !== "unchecked";
                  const isEditing = editing?.zoneIndex === zoneIndex;
                  const isHighlighted = highlightedEntity?.zoneIndex === zoneIndex;
                  return (
                    <div
                      key={`zone-${zoneIndex}`}
                      ref={isHighlighted ? highlightedRef : undefined}
                      className={`group flex items-center gap-2 rounded-md px-1.5 py-1 ${isHighlighted ? "bg-primary-50/70" : "hover:bg-gray-50"}`}
                    >
                      {isEditing ? (
                        <ExamSearchInput
                          value={editing.value}
                          onChange={(text) => setEditing({ zoneIndex, value: text })}
                          onPick={(text) => {
                            onEditZoneText(zoneIndex, text);
                            setEditing(null);
                          }}
                          enabled={key === "ExamName"}
                          autoFocus
                          onEnter={() => {
                            onEditZoneText(zoneIndex, editing.value.trim());
                            setEditing(null);
                          }}
                          onEscape={() => setEditing(null)}
                          onBlur={() => setEditing(null)}
                        />
                      ) : (
                        <button
                          type="button"
                          className={`min-w-0 flex-1 truncate text-left text-sm ${isStruck ? "text-gray-400 line-through" : isUnchecked ? "text-gray-400" : "text-gray-800"}`}
                          title="Double-cliquer pour corriger le texte"
                          onDoubleClick={() => setEditing({ zoneIndex, value: zone.text || "" })}
                        >
                          {zone.text || "—"}
                        </button>
                      )}
                      {!isEditing && <span className="shrink-0 rounded bg-gray-100 px-1 text-[10px] font-medium text-gray-500">zone</span>}
                      {isUnchecked && !isEditing && <span className="shrink-0 rounded bg-gray-100 px-1 text-[10px] font-medium text-gray-500">non coché</span>}
                      {zone.score != null && zone.score < 0.5 && !isEditing && (
                        <span className="shrink-0 rounded bg-amber-50 px-1 text-[10px] font-medium text-amber-700">faible</span>
                      )}
                      <button
                        type="button"
                        aria-label={isUnchecked ? "Annuler le non coché" : "Marquer non coché"}
                        title={isUnchecked ? "Annuler le non coché" : "Marquer comme case non cochée (exclu de l'extraction)"}
                        className={`shrink-0 rounded p-0.5 ${isUnchecked ? "text-gray-700 hover:bg-gray-100" : "text-gray-300 hover:bg-gray-100 hover:text-gray-700"}`}
                        onClick={() => onToggleZoneUnchecked(zoneIndex)}
                      >
                        <PiSquare className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        aria-label={isStruck ? "Annuler le barré" : "Marquer barré"}
                        title={isStruck ? "Annuler le barré" : "Marquer comme barré (exclu de l'extraction)"}
                        className={`shrink-0 rounded p-0.5 ${isStruck ? "text-amber-600 hover:bg-amber-50" : "text-gray-300 hover:bg-amber-50 hover:text-amber-600"}`}
                        onClick={() => onToggleZoneStruck(zoneIndex)}
                      >
                        <PiTextStrikethrough className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        aria-label="Retirer l'entité"
                        className="shrink-0 rounded p-0.5 text-gray-300 hover:bg-red-50 hover:text-red-600"
                        onClick={() => onRemoveZone(zoneIndex)}
                      >
                        <PiX className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {modifiedActs.length > 0 && (
        <div className="border-t border-gray-100 p-4">
          <div className="section-title">Production</div>
          <div className="mt-2 flex flex-col gap-1">
            {modifiedActs.map((act, index) => {
              const key = act._id || `${act.codes?.join(",")}-${act.text}`;
              const info = expandedActInfo[key];

              return (
                <div key={index}>
                  <button type="button" className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-gray-50" onClick={() => onActClick(act, key)}>
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-800">
                      {(act.codes?.length > 0 ? act.codes : act.codesPredicted).join(", ")} — {act.text}
                    </span>
                    <span className="shrink-0 text-xs text-gray-400">{act.isDeleted ? "supprimé" : "ajouté"}</span>
                  </button>
                  {info && (
                    <div className="px-1.5 pb-1 text-xs text-gray-500">
                      {info.length === 0 && "Aucune information"}
                      {info.map((item, itemIndex) => (
                        <div key={itemIndex}>{item?.exams?.map((exam) => exam.value).join(", ")}</div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default InspectorPanel;
